import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import toast from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import agentService from '../../services/agentService';
import EngagementMetrics from './components/EngagementMetrics';
import CompetitorComparison from './components/CompetitorComparison';
import PerformanceBenchmarks from './components/PerformanceBenchmarks';
import IndustryTrends from './components/IndustryTrends';
import ActionableRecommendations from './components/ActionableRecommendations';
import AgentChatInterface from '../../components/AgentChatInterface';
import ErrorBoundary from '../../components/ErrorBoundary';


const FeedbackAnalyticsAgentInterface = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, currentTeamId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState([]);
  const [activeModule, setActiveModule] = useState('engagement');
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'analytics'
  const [dateRange, setDateRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  const modules = [
    { id: 'engagement', label: 'Engagement Metrics', icon: 'Activity' },
    { id: 'competitor', label: 'Competitor Analysis', icon: 'Users' },
    { id: 'benchmarks', label: 'Performance Benchmarks', icon: 'Target' },
    { id: 'trends', label: 'Industry Trends', icon: 'TrendingUp' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' }
  ];

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await agentService?.getAnalytics(user?.id, {
        agentId: 'feedback',
        organizationId: currentTeamId
      });
      
      if (error) throw error;
      setAnalytics(data || []);
    } catch (error) {
      toast?.error('Failed to load analytics data');
      console.error('Load analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
    toast?.success('Analytics data refreshed');
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-export-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
    toast?.success('Analytics data exported');
  };

  const renderActiveModule = () => {
    switch (activeModule) {
      case 'engagement':
        return <EngagementMetrics analytics={analytics} dateRange={dateRange} />;
      case 'competitor':
        return <CompetitorComparison userId={user?.id} />;
      case 'benchmarks':
        return <PerformanceBenchmarks analytics={analytics} />;
      case 'trends':
        return <IndustryTrends userId={user?.id} />;
      case 'recommendations':
        return <ActionableRecommendations analytics={analytics} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ErrorBoundary>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
        </div>
      </ErrorBoundary>
    );
  }

  const handleTutorialStart = () => {
    // Tutorial start logic - can be customized as needed
    toast?.success('Tutorial started');
  };

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-neutral-50">
        <Toaster position="top-right" />
        <NavigationSidebar />
        
        <main className="flex-1 lg:ml-60 pb-16 lg:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-2">
                    <img 
                      src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                      alt="DraftIQ Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">Feedback & Analytics Agent</h1>
                    <p className="text-muted-foreground">Comprehensive performance insights and competitive intelligence</p>
                  </div>
                </div>
                <TutorialAccessButton onTutorialStart={handleTutorialStart} />
              </div>

              {/* Tab Switcher */}
              <div className="bg-card border border-border rounded-xl p-2 mb-6">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250
                      ${activeTab === 'chat' ?'bg-primary text-primary-foreground shadow-sm' :'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                  >
                    <Icon name="MessageSquare" size={18} />
                    <span className="font-medium">Chat with Agent</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250
                      ${activeTab === 'analytics' ?'bg-primary text-primary-foreground shadow-sm' :'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                  >
                    <Icon name="BarChart3" size={18} />
                    <span className="font-medium">Analytics Dashboard</span>
                  </button>
                </div>
              </div>

              {/* Chat Interface */}
              {activeTab === 'chat' && (
                <div className="h-[600px]">
                  <AgentChatInterface
                    userId={user?.id}
                    agentId="feedback-analytics"
                    agentName="Feedback & Analytics Agent"
                    agentIcon="BarChart3"
                    agentDescription="Performance monitoring and competitive analysis"
                  />
                </div>
              )}

              {/* Analytics Dashboard */}
              {activeTab === 'analytics' && (
                <>
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <Button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      variant="outline"
                    >
                      <Icon name={refreshing ? 'Loader2' : 'RefreshCw'} size={18} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      Refresh Data
                    </Button>
                    <Button
                      onClick={handleExportData}
                      variant="outline"
                    >
                      <Icon name="Download" size={18} className="mr-2" />
                      Export Data
                    </Button>
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e?.target?.value)}
                      className="px-4 py-2 rounded-lg border border-input bg-background text-foreground text-sm font-medium"
                    >
                      {dateRangeOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Module Tabs */}
                  <div className="bg-card border border-border rounded-xl p-4 mb-6">
                    <div className="flex flex-wrap gap-2">
                      {modules?.map((module) => (
                        <button
                          key={module?.id}
                          onClick={() => setActiveModule(module?.id)}
                          className={`
                            flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-250
                            ${activeModule === module?.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }
                          `}
                        >
                          <Icon name={module?.icon} size={18} />
                          <span className="hidden sm:inline">{module?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active Module Content */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    {renderActiveModule()}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default FeedbackAnalyticsAgentInterface;